import Vue from "vue";
import Vuex from "vuex";
import qs from "qs";
import pathify from "./pathify";
import { make } from "vuex-pathify";

const axios = require("axios");
import { Stripe } from "@/components/stripe/stripeElements";

Vue.use(Vuex);

/*
GET /api - get cart contents
POST /api/add - add a product to the cart (local only)
POST /api/applyDiscount - apply a discount code to the cart
POST /api/cart - update cart
POST /api/customer - update customer info
POST /api/shipping - update shipping info
POST /api/shippingOption - choose the way to ship
POST /api/purchase - checkout
GET /api/zoneJson - get country/zone info
*/
const validateStatus = function (status) {
  console.log(status);
  return (status >= 200 && status < 300) || status == 400;
};

async function setCart({ commit, state }) {
  const cart = await axios.get(`/api/v4?__dpd_cart=${state.cartId}`);
  commit("setCartData", cart.data);
}

async function postShippingOption({ state, commit }) {
  const { shipping } = state;

  const params = {
    shipping: shipping,
  };

  const cart = await axios.post(
    `/api/v4/shippingOption?__dpd_cart=${state.cartId}`,
    qs.stringify(params),
    { validateStatus: validateStatus }
  );

  if (cart.status == 400) {
    toastErrors(cart.data.errors);
  }

  commit("setCart", cart.data.cart);
  commit("setErrors", cart.data.errors);
}

async function applyCoupon({ commit, state }, code) {
  const params = {
    discount_code: code,
  };
  const response = await axios.post(
    `/api/v4/applyDiscount?__dpd_cart=${state.cartId}`,
    qs.stringify(params),
    { validateStatus: validateStatus }
  );

  if (response.status == 400) {
    toastErrors(response.data.errors);
  }

  commit("setCartData", response.data);
}

async function addToCart({ commit, state }, { productId, methodId }) {
  const params = {
    product_id: productId,
    method_id: methodId,
  };

  const cart = await axios.post(
    `/api/v4/add?__dpd_cart=${state.cartId}`,
    qs.stringify(params),
    { validateStatus: validateStatus }
  );
  commit("setCartData", cart.data);
}

async function postShipping({ state, commit }) {
  const { shipping } = state;

  const params = {
    shipping: shipping,
  };

  const cart = await axios.post(
    `/api/v4/shipping?__dpd_cart=${state.cartId}`,
    qs.stringify(params),
    { validateStatus: validateStatus }
  );

  if (cart.status == 400) {
    toastErrors(cart.data.errors);
  } else {
    commit("setCart", cart.data.cart);
    commit("setShipping", cart.data.shipping);
    commit("setTaxes", cart.data.taxes);
  }

  commit("setErrors", cart.data.errors);
}

function toastErrors(errors) {
  Vue.toasted.show(Object.values(errors).join(", "), {
    type: "error",
    theme: "bubble",
    position: "top-center",
    duration: 5000,
  });
}

function collectCustomFields(state) {
  if (Object.keys(state.custom_fields).length == 0) {
    return {};
  }

  return Object.assign(
    ...Object.entries(state.custom_fields).map(([k, v]) => ({ [k]: v.value }))
  );
}

async function postCustomer({ state, commit }) {
  const params = {
    customer: state.customer,
    custom_fields: collectCustomFields(state),
  };

  if (state.requires.marketing_optin.enabled === false) {
    params.customer.marketing_optin = state.requires.marketing_optin.default;
  }

  const cart = await axios.post(
    `/api/v4/customer?__dpd_cart=${state.cartId}`,
    qs.stringify(params),
    { validateStatus: validateStatus }
  );

  if (cart.data.redirect) {
    console.log("redirecting to " + cart.data.redirect);
    window.location.replace(cart.data.redirect);
  }

  if (cart.status == 400) {
    toastErrors(cart.data.errors);
  } else {
    commit("setCustomer", cart.data.customer);
    commit("setCustomFields", cart.data.custom_fields);
  }

  commit("setErrors", cart.data.errors);
}

async function getNewShippingOptions({ state, commit }) {
  const params = { shipping: state.shipping };

  //const postShipping = shipping => Vue.$http.post(config.POST_SHIPPING_URL, shipping);

  const cart = await axios.post(
    `/api/v4/shipping?__dpd_cart=${state.cartId}`,
    qs.stringify(params),
    { validateStatus: validateStatus }
  );

  if (cart.status == 400) {
    toastErrors(cart.data.errors);
    commit("setErrors", cart.data.errors);
  } else {
    commit("setShipping", cart.data.shipping);
  }
}

async function setZones({ commit, state }, countryCode) {
  // const getZones = countryCode => Vue.$http.get(`${config.GET_ZONES_URL}&country_code=${countryCode}`);
  const response = await axios.get(
    `/api/v4/zoneJson?__dpd_cart=${state.cartId}&country_code=${countryCode}`
  );
  commit("setZones", response.data);
}

async function updateProducts({ state, commit }) {
  const params = {
    items: state.cart.items,
  };

  const response = await axios.post(
    `/api/v4/cart?__dpd_cart=${state.cartId}`,
    qs.stringify(params),
    { validateStatus: validateStatus }
  );
  commit("setCartData", response.data);
}

async function purchase({ state, commit, dispatch }, gateway = null) {
  if (gateway === null) {
    gateway = state.payment.gateway;
  }
  const params = {
    credit_card: state.card,
    gateway: gateway,
    billing: state.billing,
    shipping: state.shipping,
    custom_fields: collectCustomFields(state),
    recaptcha_token: state.recaptcha_token,
  };

  const response = await axios.post(
    `/api/v4/purchase?__dpd_cart=${state.cartId}`,
    qs.stringify(params, { skipNulls: true }),
    { validateStatus: validateStatus }
  );

  if (response.data.action && response.data.action.requires_action) {
    if (response.data.action.handler == "Stripe") {
      // use the elements handleCardAction if available, otherwise fallback to the legacy Stripe version
      const handleCardAction = window?.$stripe?.stripe
        ? window.$stripe.stripe.handleCardAction
        : Stripe.handleCardAction;

      const { error: errorAction, paymentIntent } = await handleCardAction(
        response.data.action.payment_intent_client_secret
      );

      if (errorAction) {
        // Show error from Stripe.js in payment form
        alert(errorAction.message);
        console.log(errorAction);
      } else {
        commit("setCardToken", paymentIntent.id);
        await dispatch("purchase");
      }
    }
  }

  // don't commit the whole cart since it clears the page contents
  //commit('setCartData', response.data)
  commit("setErrors", response.data.errors);

  if (response.status == 400) {
    toastErrors(response.data.errors);
  } else if (response.data.redirect) {
    console.log("redirecting to " + response.data.redirect);
    window.location.replace(response.data.redirect);
  }
}

const state = {
  action: {},
  billing: {},
  cart: {
    items: [],
    discount: 0,
  },
  continue_shopping_url: null,
  culture: null,
  currency: null,
  currency_symbol: null,
  custom_fields: {
    0: {
      description: null,
      label: null,
      options: [],
      required: false,
      type: null,
      value: null,
    },
  },
  customer: {},
  discounts: [],
  errors: {},
  geo: [],
  notes: null,
  payment: {
    gateway: null,
    methods: [],
  },
  requires: {
    marketing_optin: {
      enabled: false,
      default: false,
    },
  },
  shipping: {
    options: [],
    location: {
      first_name: null,
      last_name: null,
      province: null,
      country_code: null,
    },
  },
  storefront: {
    image_url: "~assets/img/logo.png",
  },
  taxes: [],
  cartId: null,
  card: {
    card_number: null,
    exp: {
      month: null,
      year: null,
    },
    type: null,
    first_name: null,
    last_name: null,
    name: null,

    // Support credit cards identified by gateway tokens
    token: null,

    // Required for Switch & Solo cards
    start_month: null,
    start_year: null,
    issue_number: null,

    // Optional card verification value (CVV, CVV2 etc). Gateways will try their best to
    // run validation on the passed in value if it is supplied
    cvv2: null,
  },
  recaptcha_token: null,
};

const pathfy_mutations = make.mutations(state);
// console.log(pathfy_mutations);

export default new Vuex.Store({
  plugins: [pathify.plugin],
  state: state,
  getters: {
    getRedirect: (state) => state.cart.redirect,
    getSelectedPaymentMethod: (state) =>
      state.payment.methods.find(
        (method) => (state) => method.gateway == state.payment.gateway
      ),
  },
  mutations: {
    ...pathfy_mutations,
    setShipping(state, data) {
      state.shipping = data;
    },
    setTaxes(state, data) {
      state.taxes = data;
    },
    setCart(state, data) {
      state.cart = data;
    },
    setCartData(state, data) {
      if (data.action) {
        state.action = data.action;
      } else {
        state.action = {};
      }
      state.billing = data.billing;
      state.cart = data.cart;
      state.continue_shopping_url = data.continue_shopping_url;
      state.culture = data.culture;
      state.currency = data.currency;
      state.currency_symbol = data.currency_symbol;
      state.custom_fields = data.custom_fields;
      state.customer = data.customer;
      state.discounts = data.discounts;
      state.errors = data.errors;
      state.geo = data.geo;
      state.notes = data.notes;
      state.payment = data.payment;
      state.requires = data.requires;
      state.shipping = data.shipping;
      state.storefront = data.storefront;
      state.taxes = data.taxes;

      // apply the default marketing opt-in if one isn't set
      if (state.customer.marketing_optin === null) {
        state.customer.marketing_optin = state.requires.marketing_optin.default;
      }
    },
    setCardToken(state, token) {
      state.card.token = token;
    },
    setCustomer(state, customer) {
      state.customer = customer;
    },
    setCustomFields(state, custom_fields) {
      state.custom_fields = custom_fields;
    },
    setGateway(state, gateway) {
      state.payment.gateway = gateway;
    },
    setRecaptchaToken(state, token) {
      state.recaptcha_token = token;
    },
    setCartId(state, cartId) {
      state.cartId = cartId;
    },
    setCustomerCustomFields(state, data) {
      const { value, index } = data;
      state.custom_fields[index].value = value;
    },
    setErrors(state, value) {
      state.errors = value;
    },
    setCountryCode(state, value) {
      state.shipping.location.country_code = value;
    },
    setOption(state, value) {
      state.shipping.option_id = value;
    },
    setZones(state, value) {
      state.shipping.zones = value;
    },
    incrementItemQuantity(state, id) {
      const item = state.cart.items.find((item) => item.id === id);

      if (item) {
        item.quantity++;
      }
    },
    decrementItemQuantity(state, id) {
      const item = state.cart.items.find((item) => item.id === id);

      if (item && item.quantity > 0) {
        item.quantity--;
      }
    },
    removeProduct(state, id) {
      const key = state.cart.items.findIndex((item) => item.id == id);

      if (key != -1) {
        state.cart.items.splice(key, 1);
      }
    },
  },
  actions: {
    setCart,
    addToCart,
    purchase,
    incrementItemQuantity({ commit, dispatch }, id) {
      commit("incrementItemQuantity", id);
      dispatch("updateProducts");
    },
    decrementItemQuantity({ commit, dispatch }, id) {
      commit("decrementItemQuantity", id);
      dispatch("updateProducts");
    },
    updateProducts,
    removeProduct({ commit, dispatch }, productId) {
      commit("removeProduct", productId);
      dispatch("updateProducts");
    },
    postCustomer,
    postShipping,
    postShippingOption,
    setCustomerCustomFields({ commit }, { value, index }) {
      commit("setCustomerCustomFields", { value, index });
    },
    setCustomerMarketingOptin({ commit }, value) {
      commit("setCustomerMarketingOptin", value);
    },
    setCountryCode({ commit }, countryCode) {
      commit("setCountryCode", countryCode);
    },
    getNewShippingOptions,
    setZones,
    applyCoupon,
  },
});
